import { htmlZuKurztext, htmlZuText } from '../domain/htmlText';
import { appendAppLog } from '../platform/appLog';
import { pruefungsEinstellungenAblegen } from '../persistence/pruefungsEinstellungen';
import type { ExamBundle } from '../persistence/bundle';
import type {
  ActorRow,
  CandidateRow,
  ExamActorRow,
  ExamAnswerRow,
  ExamCandidateRow,
  ExamExaminerRow,
  ExamItemContainerPredefinedCommentPhraseListRow,
  ExamItemContainerRow,
  ExamItemPredefinedCommentPhraseListRow,
  ExamItemRow,
  ExamPartRow,
  ExamQuestionRow,
  ExamRow,
  ExaminerRow,
  MediaRow,
  MediaReferenceType,
  PredefinedCommentPhraseListRow,
  PredefinedCommentPhraseRow,
} from '../persistence/rows';
import type { MediumZumHolen } from './medienAblage';

/**
 * Aus dem Inhaltsbaum von Relay ein `ExamBundle` machen — **rein**, ohne Netz und ohne Ablage.
 *
 * Das Gegenstück zu `classic/abbildung.ts`, und viel kürzer: Relay' Baum **ist** unserer. Sechs
 * Ebenen hier, sechs Ebenen dort, dieselben Namen, dieselben Kennungen. Erfunden wird nichts —
 * anders als im Classic-Modus, wo `ExamPart` und `ExamQuestion` aus dem Nichts entstehen.
 *
 * ```
 * Exam ─ ExamPart ─ ExamItemContainer ─ ExamItem ─ ExamQuestion ─ ExamAnswer
 * ```
 *
 * ⚠️ **Keine Vorsätze vor den Kennungen.** Im Classic-Modus tragen sie welche, weil dort
 * fortlaufende Zahlen stehen und dieselbe `7` an zwei Servern etwas anderes bedeutet. Relay
 * vergibt UUIDs; ein Vorsatz machte sie nur unbrauchbar für `ExamLog.examID` und die
 * Dateinamen.
 */

/* ------------------------------------------------------------------ Was hereinkommt */

/** Eine Zeile aus `GET /rest/v1/exam/available` — zugleich die Kopfzeile der Prüfung. */
export interface RelayKopf {
  id?: string;
  name?: string;
  description?: string | null;
  examType?: string | null;
  published?: boolean;
  /**
   * Simulationsprüfung: Schulung, Vorführung, Demo (Relay, seit 2026-09-18). Die App schickt
   * für sie nichts hinaus; der Server filtert **nicht**.
   *
   * Steht in **beiden** Wegen: `exam/available` und im Kopf des Dokuments. Im Dokument, weil
   * das Gerät danach offline arbeitet und mitten in der Prüfung noch wissen muss, dass nichts
   * aufgezeichnet wird. Fehlt das Feld, gilt `false`.
   */
  simulative?: boolean;
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  durationSeconds?: number | null;
  tags?: string | null;
  lastModifiedDate?: string | null;
  itemCount?: number;
  mediaCount?: number;
  mediaBytes?: number;
}

export interface RelayAntwort {
  id?: string;
  orderNo?: number | null;
  answerText?: string | null;
  /**
   * Relay legt die Skala als `{name, values}` ab (`ExamDocWriter.skala`), `null` heisst keine
   * Skala. Gelesen wird auch das blosse Stufen-Array, die Form, die Stage bis 2026-11-02
   * fälschlich erwartet hat (`scalaAus`, `skalaNameAus`).
   */
  scalaValues?: unknown;
  /** Ältere Annahme: der Skalenname als eigenes Feld. Relay schickt ihn in `scalaValues.name`. */
  scalaName?: string | null;
  maxPointsNumerator?: number | null;
  maxPointsDenominator?: number | null;
}

export interface RelayFrage {
  id?: string;
  questionText?: string | null;
  vignette?: string | null;
  answerCount?: number | null;
  examAnswerListID?: string | null;
  answers?: RelayAntwort[];
}

export interface RelayItem {
  id?: string;
  name?: string | null;
  type?: string | null;
  orderNo?: number | null;
  maxPointsNumerator?: number | null;
  maxPointsDenominator?: number | null;
  shuffleAnswers?: boolean | null;
  /** Schrittweite beim Setzen von Hand (Relay `00426bb`); `null` = nicht angegeben. */
  pointsStep?: number | null;
  question?: RelayFrage | null;
  /** Die Bausteinliste **dieser Aufgabe** — ein einzelner Wert, kein Array. */
  textBlockContainerID?: string | null;
}

export interface RelayBehaelter {
  id?: string;
  type?: string | null;
  name?: string | null;
  orderNo?: number | null;
  vignette?: string | null;
  durationSeconds?: number | null;
  items?: RelayItem[];
  /** Die Bausteinliste **dieser Station** — ein einzelner Wert, kein Array. */
  textBlockContainerID?: string | null;
}

/** Eine Zeile einer Bausteinliste, in Relay' Schreibweise. */
export interface RelayBausteinzeile {
  id?: string;
  parentTextBlockLineID?: string | null;
  name?: string | null;
  /** ⚠️ `null` heisst **Gruppe**, nicht „nimm den Namen" — s. `bausteineAus`. */
  text?: string | null;
  orderNo?: number | null;
}

export interface RelayBausteinliste {
  id?: string;
  name?: string | null;
  lines?: RelayBausteinzeile[];
}

export interface RelayTeil {
  id?: string;
  name?: string | null;
  orderNo?: number | null;
  independent?: boolean | null;
  containers?: RelayBehaelter[];
}

export interface RelayAntwortliste {
  id?: string;
  name?: string | null;
  entries?: { id?: string; text?: string | null }[];
}

export interface RelayPruefling {
  candidateID?: string;
  examCandidateNo?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  matriculationNumber?: string | null;
  locale?: string | null;
  login?: string | null;
  surrogate?: boolean | null;
}

export interface RelayMedium {
  id?: string;
  name?: string | null;
  description?: string | null;
  mimeType?: string | null;
  size?: number | null;
  orderNo?: number | null;
  referenceType?: string | null;
  referenceIdentifier?: string | null;
}

/** Die Antwort von `GET /rest/v1/exam/document`. */
export interface RelayDokument {
  /**
   * Die Prüfer und Schauspieler **dieser** Prüfung, nach Nachname (seit Relay-Commit
   * `a8e198e`, 2026-10-02). Ohne Passwort und `key`. Ein älterer Relay lässt sie weg.
   */
  examiners?: RelayPersonen['examiners'];
  actors?: RelayPersonen['actors'];
  exam?: {
    id?: string;
    name?: string | null;
    durationSeconds?: number | null;
    lastModifiedDate?: string | null;
    confirmationText?: string | null;
    notice?: string | null;
    shuffleSpec?: string | null;
    /** S. `RelayKopf.simulative` — hier, weil das Gerät danach offline arbeitet. */
    simulative?: boolean;
    /** Hinweise an die Prüflinge, HTML (Relay `1901219`). Nur gespeichert, nicht angezeigt. */
    candidateInstructions?: string | null;
    /** Prüfungspasswort, Ablageform `pbkdf2-sha512$…` (Relay `00426bb`); `null` = keins. */
    devicePassword?: string | null;
  };
  parts?: RelayTeil[];
  /**
   * Die Bausteinlisten der Prüfung — **genau einmal je Liste**, auch wenn mehrere Stationen
   * oder Aufgaben auf sie zeigen.
   *
   * ⭐ **Auskunft der Relay-Seite vom 2026-09-20**, am laufenden Server nachgefahren: die Menge
   * entsteht aus einer UNION über `ExamItemContainer` **und** `ExamItem` dieser Prüfung. Eine
   * Liste, auf die nur eine Station zeigt, steht also vollständig hier — wer nur über die
   * Aufgaben liest, sieht eine Liste ohne erkennbaren Besitzer. Einen prüfungsfreien
   * Gesamtbestand gibt es bewusst nicht.
   */
  textBlockLists?: RelayBausteinliste[];
  answerLists?: RelayAntwortliste[];
  candidates?: RelayPruefling[];
  media?: RelayMedium[];
  /**
   * Einstellungen nur für diese Prüfung (Relay `8c2461a`): `{"name": "Wert als Text"}`, immer
   * da, nur was von der Vorgabe abweicht, nur Namen mit `jePruefung`.
   */
  settings?: unknown;
}

/** Die Personen zur Prüfung — seit 2026-10-02 aus dem Dokument selbst (`examiners`, `actors`). */
export interface RelayPersonen {
  examiners: { id?: string; firstName?: string | null; lastName?: string | null; groupName?: string | null }[];
  actors: { id?: string; firstName?: string | null; lastName?: string | null; groupName?: string | null }[];
}

/* ------------------------------------------------------------------- Kleinigkeiten */

/** Der Download bricht ab, statt zu raten. Die Meldung benennt die Stelle. */
export class RelayPruefungUnlesbar extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RelayPruefungUnlesbar';
  }
}

/**
 * Verbindungszeilen liefert Relay nicht als eigene Sätze — das Dokument nennt nur die
 * Personen. Die Zeile bekommt deshalb eine **abgeleitete** Kennung, damit ein zweiter
 * Download dieselbe trifft und nichts verdoppelt.
 */
export const verbindung = (examID: string, personID: string) => examID + ':' + personID;

const text = (wert: string | null | undefined) => (typeof wert === 'string' ? wert : '');
const oderNull = (wert: string | null | undefined) =>
  typeof wert === 'string' && wert !== '' ? wert : null;
const zahl = (wert: number | null | undefined, vorgabe: number) =>
  typeof wert === 'number' && Number.isFinite(wert) ? wert : vorgabe;

/**
 * Unbekannte Aufzählungswerte fallen auf den unverfänglichen Wert zurück, statt den Download
 * abzubrechen: eine Prüfung mit einer unbekannten Behälterart ist immer noch besser als keine.
 */
function ausWahl<T extends string>(wert: string | null | undefined, erlaubt: readonly T[], vorgabe: T): T {
  return typeof wert === 'string' && (erlaubt as readonly string[]).includes(wert) ? (wert as T) : vorgabe;
}

const EXAM_ARTEN = ['OSCE', 'SIMPLE', 'PROGRESS', 'BUNDLE'] as const;
const BEHAELTER_ARTEN = ['STATION', 'KEYFEATURE', 'STARSHAPED', 'EXTENDED_MATCHING'] as const;
const ITEM_ARTEN = ['TASK', 'FREETEXT', 'SINGLE_CHOICE', 'MULTIPLE_CHOICE'] as const;
const MEDIEN_BEZUEGE = [
  'EXAM', 'EXAM_PART', 'EXAM_ITEM_CONTAINER', 'EXAM_ITEM', 'EXAM_QUESTION', 'EXAM_ANSWER',
  'CANDIDATE', 'EXAMINER', 'ACTOR', 'INSTITUTION',
] as const;

/**
 * Die Stufenbeschriftungen einer Antwortzeile, als JSON-Zeichenkette für `ExamAnswer.scalaValues`.
 *
 * ⚠️ **Leer heisst Checkliste** (`persistence/readExam.ts::scoringKindOf`) — deshalb wird aus
 * einem leeren oder unbrauchbaren Wert `null` und nicht `'[]'`: eine Zeichenkette `'[]'` wäre
 * wahr und machte aus jeder Checkliste eine Kriterienleiste ohne Stufen.
 */
export function scalaAus(roh: unknown): string | null {
  const wert = typeof roh === 'string' ? sicherJson(roh) : roh;
  const liste = skalaObjekt(wert)?.values ?? wert;
  if (!Array.isArray(liste) || liste.length === 0) return null;
  const stufen = liste.filter((s): s is string => typeof s === 'string');
  return stufen.length === 0 ? null : JSON.stringify(stufen);
}

/** Relays Skalenform `{name, values}`, sonst `null`. */
function skalaObjekt(wert: unknown): { name?: unknown; values?: unknown } | null {
  return wert !== null && typeof wert === 'object' && !Array.isArray(wert) ? wert : null;
}

/** Der Skalenname einer Antwortzeile: aus `scalaValues.name`, sonst aus dem älteren Feld `scalaName`. */
export function skalaNameAus(antwort: RelayAntwort): string | null {
  const roh = antwort.scalaValues;
  const name = skalaObjekt(typeof roh === 'string' ? sicherJson(roh) : roh)?.name;
  return typeof name === 'string' ? name : (antwort.scalaName ?? null);
}

/**
 * Aus Relay' Vignettentext das, was in `ExamItemContainer.vignette` stehen darf.
 *
 * ⚠️ **Die Spalte ist bei uns kein Freitext, sondern JSON.** `readExam.ts` liest sie mit
 * `JSON.parse(...) as Vignette`; roher Text dort lässt nicht nur diese Station, sondern die
 * **ganze Prüfung** beim Lesen scheitern. In Relay ist `ExamItemContainer.vignette` dagegen
 * eine `text`-Spalte, gefüllt aus dem Editor — also HTML oder Klartext.
 *
 * Abgebildet wird wie im Classic-Modus: Klartext in `teaser` und `lead`, das **unveränderte**
 * Original als zusätzlicher Schlüssel `html`. `readExam` liest ihn nicht und stolpert nicht
 * darüber — so geht nichts verloren, wenn die Anzeige später Bilder im Text zeigen soll.
 *
 * `task` bleibt leer: Relay führt keine getrennte Aufgabenstellung am Behälter, und eine aus
 * dem Fliesstext geratene stünde im Bogen wie eine Angabe des Servers da.
 */
export function alsVignette(roh: string | null | undefined): string | null {
  if (typeof roh !== 'string' || roh.trim() === '') return null;
  // Schickt der Server eines Tages schon unsere Form, wird sie unverändert durchgereicht.
  const schon = sicherJson(roh);
  if (schon && typeof schon === 'object' && 'lead' in schon) return roh;

  const lead = htmlZuText(roh, { mitAbsaetzen: true });
  if (lead === '') return null;
  return JSON.stringify({ teaser: htmlZuKurztext(roh), lead, task: '', html: roh });
}

function sicherJson(roh: string): unknown {
  try {
    return JSON.parse(roh);
  } catch {
    return null;
  }
}

/** Nach `orderNo`, stabil bei gleichen Werten. */
function nachOrder<T extends { orderNo?: number | null }>(zeilen: T[]): T[] {
  return [...zeilen]
    .map((z, i) => ({ z, i }))
    .sort((a, b) => zahl(a.z.orderNo, a.i) - zahl(b.z.orderNo, b.i) || a.i - b.i)
    .map((e) => e.z);
}

/* ------------------------------------------------------------------- Die Abbildung */

export interface RelayAbbildung {
  bundle: ExamBundle;
  medien: MediumZumHolen[];
  /**
   * Wahr, wenn **keine** Antwortzeile Punkte oder eine Skala mitgebracht hat.
   *
   * ⚠️ Das ist der Fall, solange `Document.Answer` die drei Felder nicht führt
   * (`docs/auftrag-relay-antwortfelder.md`). Dann käme jede Aufgabe als Checkliste mit lauter
   * Einspunktzeilen an — die Oberfläche sagt es, statt still falsch zu rechnen.
   */
  ohnePunkte: boolean;
}

export function zuBundle(
  dokument: RelayDokument,
  kopf: RelayKopf,
  personen: RelayPersonen,
): RelayAbbildung {
  const examID = text(dokument.exam?.id) || text(kopf.id);
  if (examID === '') {
    throw new RelayPruefungUnlesbar('Der Server hat eine Prüfung ohne Kennung geliefert.');
  }
  // Relay schickt nur Namen mit `jePruefung`; was trotzdem anders kommt, soll auffallen.
  const einstellungen = pruefungsEinstellungenAblegen(dokument.settings);
  if (einstellungen.verworfen.length > 0) {
    void appendAppLog(
      'Prüfungseinstellungen verworfen (nicht je Prüfung setzbar oder kein Text): ' +
        einstellungen.verworfen.join(', ') + ' in Prüfung ' + examID,
      'warn',
    );
  }
  const teile = nachOrder(dokument.parts ?? []);
  if (teile.length === 0) {
    throw new RelayPruefungUnlesbar(
      'Die Prüfung hat keinen Inhalt — der Server hat kein einziges Prüfungsteil geliefert.',
    );
  }

  const exam: ExamRow = {
    id: examID,
    name: text(dokument.exam?.name) || text(kopf.name),
    description: oderNull(kopf.description),
    examType: ausWahl(kopf.examType, EXAM_ARTEN, 'OSCE'),
    durationSeconds: zahl(dokument.exam?.durationSeconds ?? kopf.durationSeconds, 0),
    startDate: text(kopf.startDate),
    startTime: text(kopf.startTime),
    endDate: oderNull(kopf.endDate),
    endTime: text(kopf.endTime),
    lastModifiedDate: text(dokument.exam?.lastModifiedDate) || text(kopf.lastModifiedDate),
    notice: oderNull(dokument.exam?.notice),
    /**
     * ⚠️ **Das Dokument schlägt den Katalog, und beides zählt.** Der Server schickt das Feld in
     * beiden Wegen; steht es im Dokument, gilt das — es ist der jüngere Stand und der, den das
     * Gerät offline noch hat. Fehlt es in beiden, ist die Prüfung **echt**: im Zweifel wird
     * aufgezeichnet, nicht verworfen. Die andere Vorgabe wäre die gefährliche.
     */
    simulative: (dokument.exam?.simulative ?? kopf.simulative) === true ? 'Y' : 'N',
    // Roh gespeichert: bereinigt wird erst beim Anzeigen (`domain/htmlSicher.ts`). Nur Leerraum
    // ist kein Hinweis.
    candidateInstructions:
      typeof dokument.exam?.candidateInstructions === 'string' && dokument.exam.candidateInstructions.trim() !== ''
        ? dokument.exam.candidateInstructions
        : null,
    devicePassword: oderNull(dokument.exam?.devicePassword),
    settings: einstellungen.ablage,
  };

  const parts: ExamPartRow[] = [];
  const containers: ExamItemContainerRow[] = [];
  const items: ExamItemRow[] = [];
  const questions: ExamQuestionRow[] = [];
  const answers: ExamAnswerRow[] = [];
  const itemPhraseLists: ExamItemPredefinedCommentPhraseListRow[] = [];
  const containerPhraseLists: ExamItemContainerPredefinedCommentPhraseListRow[] = [];
  /** Welche Bausteinlisten wirklich gebraucht werden — s. `bausteineAus`. */
  const gebrauchteListen = new Set<string>();
  let mitPunkten = false;

  const bausteinlisten = new Map<string, RelayBausteinliste>();
  for (const liste of dokument.textBlockLists ?? []) {
    if (liste.id) bausteinlisten.set(liste.id, liste);
  }
  /**
   * Einen Bezug auf eine Bausteinliste vermerken.
   *
   * ⚠️ **Nur, wenn die Liste auch mitgeliefert wurde.** Ein Verweis ins Leere ergäbe eine
   * Verknüpfung auf eine Liste, die es nicht gibt — auf dem Gerät ein verletzter
   * Fremdschlüssel, im Browser eine Leiste, die sich nicht öffnen lässt.
   */
  const merkeListe = (roh: string | null | undefined): string | null => {
    const id = text(roh);
    if (id === '' || !bausteinlisten.has(id)) return null;
    gebrauchteListen.add(id);
    return id;
  };

  const listen = new Map<string, RelayAntwortliste>();
  for (const liste of dokument.answerLists ?? []) {
    if (liste.id) listen.set(liste.id, liste);
  }

  teile.forEach((teil, teilNr) => {
    const partID = text(teil.id);
    if (partID === '') return;
    parts.push({ id: partID, examID, name: text(teil.name), orderNo: zahl(teil.orderNo, teilNr) });

    nachOrder(teil.containers ?? []).forEach((behaelter, behNr) => {
      const containerID = text(behaelter.id);
      if (containerID === '') return;
      containers.push({
        id: containerID,
        examPartID: partID,
        name: text(behaelter.name),
        type: ausWahl(behaelter.type, BEHAELTER_ARTEN, 'STATION'),
        durationSeconds: typeof behaelter.durationSeconds === 'number' ? behaelter.durationSeconds : null,
        orderNo: zahl(behaelter.orderNo, behNr),
        vignette: alsVignette(behaelter.vignette),
        // ⚠️ `comment` führt der Vertrag nicht. Leer lassen statt die Vignette hineinkopieren:
        // eine erfundene Doppelung sähe am Bogen aus wie eine Angabe des Servers.
        comment: null,
      });

      const stationsliste = merkeListe(behaelter.textBlockContainerID);
      if (stationsliste !== null) {
        containerPhraseLists.push({
          id: containerID + ':' + stationsliste,
          examItemContainerID: containerID,
          predefinedCommentPhraseListID: stationsliste,
        });
      }

      nachOrder(behaelter.items ?? []).forEach((item, itemNr) => {
        const itemID = text(item.id);
        if (itemID === '') return;
        items.push({
          id: itemID,
          examItemContainerID: containerID,
          name: text(item.name),
          type: ausWahl(item.type, ITEM_ARTEN, 'TASK'),
          orderNo: zahl(item.orderNo, itemNr),
          maxPointsNumerator: zahl(item.maxPointsNumerator, 1),
          maxPointsDenominator: zahl(item.maxPointsDenominator, 1),
          pointsStep:
            typeof item.pointsStep === 'number' && Number.isFinite(item.pointsStep) && item.pointsStep > 0
              ? item.pointsStep
              : null,
          // ⛔ `anchorCriterion` bleibt leer — es gehört zur Lösung.
          //
          // ⬅️ **Richtigstellung vom 2026-09-17:** hier stand „hält der Server bewusst
          // zurück". Das gilt nicht mehr. Der Nutzer der Relay-Seite hat am 2026-09-16
          // entschieden, dass `/exam/document` **alles** ausliefert — `correct`, `solution`,
          // `rationale`, `anchorCriterion`, `correctness`, dazu `password` als PBKDF2-Hash und
          // `otpData`; geschützt wird der Zugang durch TLS und das Gerätepasswort.
          //
          // ⚠️ **Wer filtert, sind seitdem wir.** Diese Abbildung ist eine Positivliste: sie
          // baut jede Zeile Feld für Feld, und die Lösungsfelder stehen in keiner davon —
          // `ExamAnswer` hat nicht einmal eine Spalte dafür. Die Lösung erreicht die Platte des
          // Prüfergeräts also nicht. Das ist jetzt **unsere** Zusage, nicht die des Servers,
          // und wer hier ein Feld ergänzt, hebt sie auf.
          anchorCriterion: null,
          comment: null,
        });

        // ⚠️ **Vor dem Ausstieg bei Aufgaben ohne Frage.** Eine Checkliste ohne `question`
        // verlässt den Rundgang gleich darunter — ihre Bausteine gingen sonst verloren.
        const aufgabenliste = merkeListe(item.textBlockContainerID);
        if (aufgabenliste !== null) {
          itemPhraseLists.push({
            id: itemID + ':' + aufgabenliste,
            examItemID: itemID,
            predefinedCommentPhraseListID: aufgabenliste,
          });
        }

        const frage = item.question;
        if (!frage?.id) return;
        const antworten = antwortenDerFrage(frage, listen);
        /*
         * ⚠️ **Relay schreibt eine globale Bewertung als Zeile** (Skalenname `ims.osce.global`,
         * Classic- und QTI-Import), Stage erkennt sie daran, dass es **keine** `ExamAnswer` gibt
         * (`readExam.ts::scoringKindOf`). Wie im Classic-Weg verschwinden die Zeilen also, ihr
         * Text wird der markierbare Fragetext; Höchstpunkte und Schritt trägt das Item.
         * Eine Mischung mit anderen Zeilen bleibt Checkliste.
         */
        const global = antworten.length > 0 && antworten.every((a) => skalaNameAus(a) === 'ims.osce.global');
        const block = global
          ? antworten
              .map((a) => htmlZuText(a.answerText ?? '', { mitAbsaetzen: true }))
              .filter((t) => t !== '')
              .join('\n')
          : '';
        questions.push({
          id: frage.id,
          examItemID: itemID,
          // Klartext: der Fragetext ist der Text der globalen Bewertung, und dort markiert der
          // Prüfer einzelne Wörter — Tags wären „Wörter" (2026-10-02). Antworten bleiben HTML.
          questionText: oderNull(block || htmlZuText(frage.questionText ?? '', { mitAbsaetzen: true })),
          vignette: oderNull(frage.vignette),
        });

        for (const [nr, antwort] of (global ? [] : antworten).entries()) {
          const antwortID = text(antwort.id);
          if (antwortID === '') continue;
          const scala = scalaAus(antwort.scalaValues);
          if (scala !== null || typeof antwort.maxPointsNumerator === 'number') mitPunkten = true;
          answers.push({
            id: antwortID,
            examQuestionID: frage.id,
            answerText: oderNull(antwort.answerText),
            orderNo: zahl(antwort.orderNo, nr),
            scalaValues: scala,
            maxPointsNumerator: zahl(antwort.maxPointsNumerator, 1),
            maxPointsDenominator: zahl(antwort.maxPointsDenominator, 1),
            comment: null,
          });
        }
      });
    });
  });

  /* ---- Personen ---- */

  const examiners: ExaminerRow[] = [];
  const examExaminers: ExamExaminerRow[] = [];
  for (const p of personen.examiners) {
    if (!p.id) continue;
    examiners.push({
      id: p.id,
      firstName: text(p.firstName),
      lastName: text(p.lastName),
      groupName: text(p.groupName),
      key: null,
      // ⚠️ Der Server gibt kein Passwort heraus, und das ist richtig so: die App meldet keine
      // Menschen an (`docs/spec-phase4-server.md` §6).
      password: '',
    });
    examExaminers.push({ id: verbindung(examID, p.id), examID, examinerID: p.id });
  }

  const actors: ActorRow[] = [];
  const examActors: ExamActorRow[] = [];
  for (const p of personen.actors) {
    if (!p.id) continue;
    actors.push({
      id: p.id,
      firstName: text(p.firstName),
      lastName: text(p.lastName),
      groupName: oderNull(p.groupName),
    });
    examActors.push({ id: verbindung(examID, p.id), examID, actorID: p.id });
  }

  const candidates: CandidateRow[] = [];
  const examCandidates: ExamCandidateRow[] = [];
  for (const p of dokument.candidates ?? []) {
    const id = text(p.candidateID);
    if (id === '') continue;
    candidates.push({
      id,
      firstName: text(p.firstName),
      lastName: text(p.lastName),
      matriculationNumber: oderNull(p.matriculationNumber),
    });
    examCandidates.push({
      id: verbindung(examID, id),
      examID,
      candidateID: id,
      examCandidateNo: oderNull(p.examCandidateNo),
    });
  }

  /* ---- Medien ---- */

  const media: MediaRow[] = [];
  const medien: MediumZumHolen[] = [];
  nachOrder(dokument.media ?? []).forEach((m, nr) => {
    const id = text(m.id);
    const bezug = text(m.referenceIdentifier);
    if (id === '' || bezug === '') return;
    media.push({
      id,
      name: text(m.name),
      mimeType: oderNull(m.mimeType),
      referenceType: ausWahl<MediaReferenceType>(m.referenceType, MEDIEN_BEZUEGE, 'EXAM'),
      referenceIdentifier: bezug,
      // ⚠️ Von der App **nicht** belegt (s. `persistence/rows.ts`).
      referenceSubType: null,
      size: zahl(m.size, 0),
      orderNo: zahl(m.orderNo, nr),
      description: oderNull(m.description),
    });
    medien.push({ id, mime: text(m.mimeType) });
  });

  const { phraseLists, phrases } = bausteineAus(bausteinlisten, gebrauchteListen);

  return {
    bundle: {
      exam, parts, containers, items, questions, answers,
      examiners, examExaminers, actors, examActors, candidates, examCandidates,
      media,
      phraseLists, phrases, itemPhraseLists, containerPhraseLists,
    },
    medien,
    ohnePunkte: answers.length > 0 && !mitPunkten,
  };
}

/**
 * Aus Relay' Bausteinlisten unsere zwei Tabellen.
 *
 * ⭐ **Die Form stammt aus dem laufenden Server** (Auskunft der Relay-Seite, 2026-09-20), nicht
 * aus unserem Vorschlag. Drei Abweichungen, alle ausdrücklich genannt:
 *
 * | Unser Vorschlag | Ihre Form |
 * | --- | --- |
 * | `parentPredefinedCommentPhraseID`, `fullText` | `parentTextBlockLineID`, `text` |
 * | fehlt `fullText` → der Name ist der Baustein | `text: null` **heisst Gruppe** |
 * | mehrere Listen je Aufgabe | **genau eine**, als Fremdschlüssel |
 *
 * ⚠️ **Nur gebrauchte Listen.** Eine Liste, auf die weder Station noch Aufgabe zeigt, wäre ein
 * Baum, den keine Oberfläche je zeigt.
 *
 * ⚠️ **Eltern vor ihren Kindern.** `parentPredefinedCommentPhraseID` verweist auf dieselbe
 * Tabelle; ein Kind vor seinem Elternteil verletzt den Fremdschlüssel — im Browser unsichtbar,
 * auf dem Gerät nicht.
 */
function bausteineAus(
  listen: ReadonlyMap<string, RelayBausteinliste>,
  gebraucht: ReadonlySet<string>,
): { phraseLists: PredefinedCommentPhraseListRow[]; phrases: PredefinedCommentPhraseRow[] } {
  const phraseLists: PredefinedCommentPhraseListRow[] = [];
  const phrases: PredefinedCommentPhraseRow[] = [];

  for (const [listenID, liste] of listen) {
    if (!gebraucht.has(listenID)) continue;
    phraseLists.push({ id: listenID, name: text(liste.name) });

    const zeilen = (liste.lines ?? []).filter((z) => text(z.id) !== '');
    const vorhanden = new Set(zeilen.map((z) => text(z.id)));
    const hatKinder = new Set<string>();
    for (const z of zeilen) {
      const eltern = text(z.parentTextBlockLineID);
      if (eltern !== '' && vorhanden.has(eltern)) hatKinder.add(eltern);
    }

    /**
     * ⚠️ **Eine Zeile ohne Text und ohne Kinder ist weder Gruppe noch Baustein.** Wegwerfen —
     * aber **nicht still**: die Pflegemaske der Serverseite kann so etwas anlegen, und sie will
     * davon erfahren. Es ist ein Hinweis auf eine halbfertige Liste in der Verwaltung, kein
     * Übertragungsfehler.
     */
    const brauchbar = zeilen.filter((z) => {
      const id = text(z.id);
      if (text(z.text) !== '' || hatKinder.has(id)) return true;
      void appendAppLog(
        'Baustein ohne Text und ohne Kinder: "' + text(z.name) + '" (' + id + ') in Liste "' +
          text(liste.name) + '" (' + listenID + ') — halbfertige Liste in der Verwaltung?',
        'warn',
      );
      return false;
    });

    const bleiben = new Set(brauchbar.map((z) => text(z.id)));
    const elternVon = (z: RelayBausteinzeile): string | null => {
      const eltern = text(z.parentTextBlockLineID);
      // Ein Elternteil, das nicht mitkam, zählt als Wurzel — sonst verschwände der Eintrag.
      return eltern !== '' && bleiben.has(eltern) ? eltern : null;
    };

    /**
     * Wie tief eine Zeile hängt. Danach wird stabil sortiert: erst alle Wurzeln, dann deren
     * Kinder, und so fort.
     *
     * ⚠️ **Gegen Kreise gesichert.** Zwei Zeilen, die sich gegenseitig als Elternteil führen,
     * drehten sich hier ewig; sie zählen als Wurzeln. Der Baumbau sichert sich noch einmal
     * selbst ab (`domain/bausteine.ts`) — ein Dokument kann aus einer älteren Quelle stammen.
     */
    const nachId = new Map(brauchbar.map((z) => [text(z.id), z]));
    const tiefeVon = (z: RelayBausteinzeile): number => {
      const gesehen = new Set<string>([text(z.id)]);
      let tiefe = 0;
      let eltern = elternVon(z);
      while (eltern !== null && !gesehen.has(eltern)) {
        gesehen.add(eltern);
        tiefe += 1;
        const naechste = nachId.get(eltern);
        eltern = naechste ? elternVon(naechste) : null;
      }
      return tiefe;
    };

    const sortiert = brauchbar
      .map((z, nr) => ({ z, nr, tiefe: tiefeVon(z) }))
      .sort((a, b) => a.tiefe - b.tiefe || a.nr - b.nr);

    for (const { z } of sortiert) {
      phrases.push({
        id: text(z.id),
        predefinedCommentPhraseListID: listenID,
        parentPredefinedCommentPhraseID: elternVon(z),
        name: text(z.name),
        // `text: null` heisst Gruppe; der Baumbau macht daraus eine Überschrift, weil sie
        // Kinder hat.
        fullText: text(z.text),
        orderNo: zahl(z.orderNo, 0),
      });
    }
  }

  return { phraseLists, phrases };
}

/**
 * Die Antworten einer Frage — eigene zuerst, sonst die der Antwortliste.
 *
 * So führt Relay geteilte Skalen: mehrere Fragen zeigen auf **eine** `ExamAnswerList`, statt
 * dieselben fünf Stufen hundertmal abzulegen. Kommt die Frage ohne eigene Zeilen, sind die
 * Einträge der Liste ihre Antworten.
 */
function antwortenDerFrage(
  frage: RelayFrage,
  listen: Map<string, RelayAntwortliste>,
): RelayAntwort[] {
  const eigene = nachOrder(frage.answers ?? []).filter((a) => a.id);
  if (eigene.length > 0) return eigene;

  const liste = frage.examAnswerListID ? listen.get(frage.examAnswerListID) : undefined;
  if (!liste) return [];
  return (liste.entries ?? [])
    .filter((e) => e.id)
    .map((e, nr) => ({ id: e.id, orderNo: nr, answerText: e.text }));
}
